<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { CatalogueBrowserMode } from '@/models/Catalogue';
import type {
  CatalogueItem,
  CatalogueItemGroup,
  ChildItemData,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import CatalogueItemBrowser from '@/components/catalogue/CatalogueItemBrowser.vue';

const { $gettext } = gettext;

const props = defineProps<{
  modelValue: boolean;
  parentUuid: string;
  groups: CatalogueItemGroup[];
  existingChildUuids?: string[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', childData: ChildItemData): void;
}>();

const browserRef = ref<InstanceType<typeof CatalogueItemBrowser> | null>(null);
const selectedItem = ref<CatalogueItem | null>(null);
const quantity = ref(1);

// Compute excluded UUIDs (parent + existing children)
const excludedUuids = computed(() => [
  props.parentUuid,
  ...(props.existingChildUuids || []),
]);

const handleSelect = (item: CatalogueItem) => {
  selectedItem.value = item;
};

const handleSave = () => {
  if (selectedItem.value) {
    emit('save', {
      uuid: selectedItem.value.uuid,
      quantity: quantity.value,
    });
    emit('update:modelValue', false);
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      selectedItem.value = null;
      quantity.value = 1;
      // Reset the browser state
      browserRef.value?.reset();
    }
  },
);
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="600px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="pa-6 bg-white" style="border-radius: 8px">
      <div class="d-flex align-center mb-4">
        <h2 class="text-h5">{{ $gettext('Add Component') }}</h2>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleClose" />
      </div>

      <CatalogueItemBrowser
        ref="browserRef"
        :mode="CatalogueBrowserMode.SELECT"
        :groups="groups"
        :items-per-page="24"
        :exclude-uuids="excludedUuids"
        :auto-load="modelValue"
        density="compact"
        selectable
        @select="handleSelect"
      />

      <!-- Quantity input (shown when item is selected) -->
      <v-text-field
        v-if="selectedItem"
        v-model.number="quantity"
        :label="$gettext('Quantity')"
        type="number"
        min="1"
        class="mt-4"
      />

      <div class="d-flex justify-end mt-6">
        <v-btn variant="text" class="mr-2" @click="handleClose">
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="text"
          :disabled="!selectedItem"
          @click="handleSave"
        >
          {{ $gettext('Add Component') }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>
